import withStore, {
  TPresentable,
  TLoadingAwarePresenter
} from "../../core/presenter/withStore";
import Application from "../../Application";
import { IUser } from "../../user/model/User";
import { IOrganisation } from "../../organisations/model/Organisation";
import { IOrganisationIndexCategory } from "../../organisations/model/OrganisationIndexCategory";
import GetAllOrganisationIndexCategoriesInteractor from "../../organisations/interactors/GetAllOrganisationIndexCategoriesInteractor";
import { IIndexHarmonizationReportItem } from "../model/IndexHarmonizationReportItem";
import GetIndexNonHarmonizedReportInteractor from "../interactor/GetIndexNonHarmonizedReportInteractor";

export interface TIndexNonHarmonizedReportPresenter
  extends TLoadingAwarePresenter {
  user?: IUser;
  mainOrganisationId: number;
  organisationIds: number[];
  organisations: IOrganisation[];
  organisationIndexCategories: IOrganisationIndexCategory[];
  dateIncluded?: Date;
  selectedCategoryId: number;
  data: IIndexHarmonizationReportItem[];
}

export interface IIndexNonHarmonizedReportPresenter
  extends TIndexNonHarmonizedReportPresenter,
    TPresentable {
  loadReferenceValues(organisations: IOrganisation[]): void;
  setMainOrganisationId(organisationId: number): void;
  setDateIncluded(dateIncluded: Date): void;
  setOrganisationIds(organisationIds: number[]): void;
  setCategoryId(categoryId: number): void;
  getData(): void;
}

const defaultState: TIndexNonHarmonizedReportPresenter = {
  mainOrganisationId: NaN,
  selectedCategoryId: NaN,
  organisationIds: [],
  organisations: [],
  organisationIndexCategories: [],
  dateIncluded: undefined,
  data: []
};

const IndexNonHarmonizedReportPresenter = withStore<
  IIndexNonHarmonizedReportPresenter,
  TIndexNonHarmonizedReportPresenter
>(
  ({
    application,
    store,
    loader,
    translate
  }): IIndexNonHarmonizedReportPresenter => {
    const _store = store;
    const _application: Application = application;
    const state = _store.getState<
      TIndexNonHarmonizedReportPresenter & TLoadingAwarePresenter
    >();
    loader.start("projectLoader");
    const loadReferenceValues = (organisations: IOrganisation[]) => {
      const user = _store.getState<any>().user;
      _store.update({ organisations, user });
      user.organisationId && setMainOrganisationId(user.organisationId);
      loader.stop("projectLoader");
      loader.stop("reportLoader");
    };
    const setMainOrganisationId = async (organisationId: number) => {
      if (organisationId) {
        getOrganisationIndexCategories(organisationId);
      }
      _store.update({ mainOrganisationId: organisationId, data: undefined });
    };
    const getOrganisationIndexCategories = async (organisationId: number) => {
      let data = await _application.container
        .resolve<GetAllOrganisationIndexCategoriesInteractor>(
          "getAllOrganisationIndexCategories"
        )
        .execute(organisationId);
      _store.update({
        organisationIndexCategories: data,
        selectedCategoryId: undefined,
        data: undefined
      });
    };
    const setDateIncluded = (dateIncluded: Date) => {
      _store.update({ dateIncluded, data: undefined });
    };
    const setCategoryId = (categoryId: number) => {
      _store.update({ selectedCategoryId: categoryId, data: undefined });
    };
    const setOrganisationIds = (organisationIds: number[]) => {
      _store.update({ organisationIds, data: undefined });
    };
    const getData = async () => {
      const { selectedCategoryId } =
        _store.getState<TIndexNonHarmonizedReportPresenter>();
      loader.start("reportLoader");
      let data = await _application.container
        .resolve<GetIndexNonHarmonizedReportInteractor>(
          "getIndexNonHarmonizedReport"
        )
        .execute(selectedCategoryId);
      _store.update({ data });
      loader.stop("reportLoader");
    };
    return {
      ...state,
      store: _store,
      loader,
      application: _application,
      translate,
      loadReferenceValues,
      setMainOrganisationId,
      setDateIncluded,
      setOrganisationIds,
      getData,
      setCategoryId
    };
  },
  defaultState
);

export default IndexNonHarmonizedReportPresenter;
